import { usePageTitle } from '../lib/usePageTitle';
import { useInstallPrompt } from '../lib/useInstallPrompt';
import { useAnnouncer } from '../components/LiveAnnouncer';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/Button';

/**
 * Come installare PandaCalcio come app (PWA) su Android e iOS.
 * Pubblica: raggiungibile anche senza login. Su Android, se il browser lo
 * consente, offriamo direttamente il pulsante di installazione nativo.
 */
export function InstallPage() {
  usePageTitle('Installa l’app');
  const { canPrompt, install, installed } = useInstallPrompt();
  const { announce } = useAnnouncer();

  return (
    <AuthLayout title="Porta PandaCalcio in tasca">
      <img
        src="/mascotte.webp"
        alt=""
        width={160}
        height={160}
        className="mx-auto -mt-2 mb-4 h-40 w-40"
      />
      <p className="text-lg">
        PandaCalcio si installa come una vera app, direttamente dal browser: niente store,
        aggiornamenti automatici, si apre a schermo intero con la sua icona.
      </p>

      {installed && (
        <p role="status" className="mt-4 rounded-lg border-2 border-success bg-panda-50 p-3 font-medium text-success dark:bg-ink-800">
          <span aria-hidden="true">✓ </span>PandaCalcio è già installata su questo dispositivo!
        </p>
      )}

      {!installed && canPrompt && (
        <div className="mt-5">
          <Button
            onClick={async () => {
              const outcome = await install();
              announce(
                outcome === 'accepted'
                  ? 'App installata! La trovi nella schermata Home.'
                  : 'Installazione annullata: puoi riprovare quando vuoi.',
              );
            }}
          >
            Installa l’app adesso
          </Button>
          <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
            Un tocco e il panda è nella tua schermata Home. 🐼
          </p>
        </div>
      )}

      {!installed && (
        <>
          <section aria-labelledby="install-android" className="mt-8">
            <h2 id="install-android" className="text-xl font-bold tracking-tight">
              <span aria-hidden="true">🤖 </span>Su Android (Chrome)
            </h2>
            <ol className="mt-3 flex list-decimal flex-col gap-2 pl-5">
              <li>
                Apri <strong>pandacalcio.it</strong> con Chrome
              </li>
              <li>
                Tocca il menu <strong>⋮</strong> (tre puntini) in alto a destra
              </li>
              <li>
                Scegli <strong>«Aggiungi a schermata Home»</strong> oppure{' '}
                <strong>«Installa app»</strong>
              </li>
              <li>
                Conferma con <strong>Installa</strong>
              </li>
            </ol>
          </section>

          <section aria-labelledby="install-apk" className="mt-8">
            <h2 id="install-apk" className="text-xl font-bold tracking-tight">
              <span aria-hidden="true">📦 </span>App nativa Android (APK)
            </h2>
            <p className="mt-3">
              Preferisci l’app nativa? Scarica l’APK e installalo: è la stessa PandaCalcio, con
              icona e schermata di avvio del panda, e si aggiorna da sola a ogni novità del sito.
            </p>
            <p className="mt-3">
              <a
                href="/pandacalcio.apk"
                className="button inline-flex min-h-11 items-center justify-center rounded-xl bg-panda-700 px-6 py-2.5 font-semibold text-white no-underline hover:bg-panda-800 dark:bg-panda-600"
              >
                Scarica l’APK per Android
              </a>
            </p>
            <p className="mt-3 text-sm text-ink-600 dark:text-ink-300">
              Alla prima installazione Android chiede di consentire le app da «origini
              sconosciute» per il browser: è normale per le app fuori dal Play Store — conferma e
              prosegui.
            </p>
          </section>

          <section aria-labelledby="install-ios" className="mt-8">
            <h2 id="install-ios" className="text-xl font-bold tracking-tight">
              <span aria-hidden="true">🍎 </span>Su iPhone e iPad (Safari)
            </h2>
            <ol className="mt-3 flex list-decimal flex-col gap-2 pl-5">
              <li>
                Apri <strong>pandacalcio.it</strong> con Safari
              </li>
              <li>
                Tocca il pulsante <strong>Condividi</strong>{' '}
                <span aria-hidden="true">(il quadrato con la freccia verso l’alto)</span>
              </li>
              <li>
                Scorri e scegli <strong>«Aggiungi alla schermata Home»</strong>
              </li>
              <li>
                Tocca <strong>Aggiungi</strong> in alto a destra
              </li>
            </ol>
            <p className="mt-3 text-sm text-ink-600 dark:text-ink-300">
              Nota: su iPhone funziona solo da Safari — da Chrome o altri browser la voce non
              appare.
            </p>
          </section>

          <section aria-labelledby="install-desktop" className="mt-8">
            <h2 id="install-desktop" className="text-xl font-bold tracking-tight">
              <span aria-hidden="true">💻 </span>Su computer (Chrome ed Edge)
            </h2>
            <p className="mt-3">
              Nella barra dell’indirizzo appare un’icona di installazione{' '}
              <span aria-hidden="true">(un monitor con una freccia)</span>: cliccala e conferma con{' '}
              <strong>Installa</strong>.
            </p>
          </section>
        </>
      )}
    </AuthLayout>
  );
}
